import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import store from '../../../redux/store';
import {checkTutorialActionWouldApply} from '../../../utils/tutorial/tutorialActionGate';
import {
  completePhase,
  confirmCombatStage,
  confirmSetupPhase,
  showGameEndModal,
  showNoConstructionActionsModal,
  showSupplyFarmsConfirmModal,
  showToast,
  showToastWaitOtherPlayerTurn,
  showWarStageUnitsConfirmModal,
  startGame,
  startNextPhase
} from '../../../redux/actions';
import {
  selectCurrentPlayer,
  selectGameResults,
  selectGameType,
  selectGroundBackground,
  selectHexes,
  selectIsGameStarted,
  selectIsInitiativeInterceptedWithIsolationism,
  selectIsParallelSupply,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPendingMeleeDamage,
  selectPhase,
  selectPlayers,
  selectPlayersConfirmedCombatStage,
  selectPlayersConfirmedSetupPhase,
  selectPlayersWaitedToConfirmCombatStage,
  selectRound,
  selectSharedGameState,
  selectStandardTurnDurationMs,
  selectStartingQuarters,
  selectTimerEnabled,
  selectTurnDurationMs,
  selectTurnEndTimestamp,
  selectUnits,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {formatTurnTimeLeftMinutes} from "../../../utils/phase/aiTurnTimerText";
import {getMsToNextTurnTimerTick} from "../../../utils/phase/getMsToNextTurnTimerTick";
import {
  checkIfTurnInputBlocked,
  notifyTurnInputBlocked,
  selectIsTurnInputLocked,
} from "../../../utils/gameState/turn/turnInputLock";
import './CurrentPlayerSection.css'
import {PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {WIN_POINTS_COLOR_DARK} from "../../../styles/constants";
import PhaseAutoComplitioner from "../PhaseAutoComplitioner/PhaseAutoComplitioner";
import {checkIfEnterKeyHandledMode, useCompletePhaseEnterKey} from "./useCompletePhaseEnterKey";
import LocalTurnTimer from "../LocalTurnTimer/LocalTurnTimer";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {isEnvConfEnabled} from "../../../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../../constants/env";
import {HOT_SEAT_COMPLETE_PHASE_COOLDOWN} from "../../../constants/hotSeat";
import {useIsCurrentUserTurn} from "../../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {GAME_TYPES} from "../../../constants/game";
import {
  selectIsTutorialActive,
  selectServerTimeDelta,
  selectUserId
} from "../../../redux/reducers/privateStateReducer/selectors";
import {checkIfParallelTurn} from "../../../utils/gameState/turn/checkIfParallelTurn";
import {getActingPlayer, getActingPlayerFromState} from "../../../utils/players/getActingPlayer";
import {getRandomInitiativeShift} from "../../../utils/gameState/getRandomInitiativeShift";
import {getRandomGroundBackground} from "../../../configs/groundBackgrounds";
import {TEXT_SETUP_STARTING_QUARTER, TEXT_SETUP_STARTING_QUARTER_WAIT_OTHERS} from "../../../constants/text/setup";
import {TEXT_SUPPLY_WAIT_OTHERS} from "../../../constants/text/supply";
import {
  checkIfAllPlayersConfirmedStartingQuarter
} from "../../../utils/gameState/setup/checkIfAllPlayersConfirmedStartingQuarter";
import {TEXT_COMBAT_STAGE_WAIT_OTHERS} from "../../../constants/text/combat";
import {
  checkIfAllPlayersConfirmedCombatStage
} from "../../../utils/gameState/melee/checkIfAllPlayersConfirmedCombatStage";
import {getHumanMeleeCompletingPlayer} from "../../../utils/gameState/melee/getHumanMeleeCompletingPlayer";
import {getMeleeRerollUnitIds} from "../../../utils/gameState/melee/getMeleeRerollUnitIds";

import {executeWarPhaseStageCompletion, handleCompleteTurn} from "../../../utils/gameState/turn/handleCompleteTurn";
import {checkIfPlayerConfirmedSetupPhase} from "../../../utils/gameState/setup/checkIfPlayerConfirmedSetupPhase";
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";
import {getRandomPolicyForMarket} from "../../../utils/policy/getRandomPolicyForMarket";
import {createPhaseActionGuard} from "../../../utils/gameState/turn/phaseActionGuard";
import {CARD_OPEN_SOURCES, usePlayersListSideMenu} from "../../../contexts/PlayersListSideMenuContext";
import {useReplayControls} from "../../../contexts/ReplayControlsContext";
import {SPECTATOR_ID} from "../../../constants/players";
import {calcUsedConstructionActions} from "../../../utils/gameState/construction/calcUsedConstructionActions";
import {checkIfPlayerHasSupplyCapableFarm} from "../../../utils/gameState/checkIfPlayerHasSupplyCapableFarm";
import {UNIT_AUTO_SELECTION_STAGES} from "../../../utils/unitAutoSelection/checkIfUnitAutoSelectionActive";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer,
  selectTurnDisplayedPlayer
} from "../../../utils/gameState/supply/parallelSupplySelectors";
import {checkIfPlayerCompletedPhase} from "../../../utils/gameState/supply/parallelSupply";
import {selectPhaseProgress} from "../../../utils/gameState/progress/selectPhaseProgress";
import {COMPLETE_BUTTON_MODES, getCompleteButtonView} from "../../../utils/gameUi/getCompleteButtonView";
import {getCompleteButtonColor} from "../../../utils/gameUi/getCompleteButtonColor";
import {useCompleteButtonPulse} from "./CompleteButtonParts";
import CompletePhaseButtonPlate from "./CompletePhaseButtonPlate";

const TIME_REMAINING_FOR_TIMER_SOUND_SEC = 4;
const TIME_REMAINING_FOR_TIMER_SOUND_MS = TIME_REMAINING_FOR_TIMER_SOUND_SEC * 1000;
const REPLAY_CLICK_DISAMBIGUATION_MS = 300;
// До какого раунда включительно предупреждаем о завершении хода без единого
// действия в фазе строительства (позже в партии осознанный пропуск — обычное дело)
const NO_CONSTRUCTION_ACTIONS_WARNING_MAX_ROUND = 10;

// Иконка кнопки: галочка — ходить тебе, часы — ждем чужого хода. Часы
// рисует анимированный HourglassIcon, но выбор ветки по-прежнему идет по пути
// картинки — так все ветки читаются одинаково
const CHECK_ICON_PATH = '/img/icons/check.png';
const HOURGLASS_ICON_PATH = '/img/icons/hourglass.png';

const CompletePhaseButton = ({isDemoMode = false, isSpectatorMode = false}) => {
  const dispatch = useDispatch();
  const gameType = useSelector(selectGameType);
  const userId = useSelector(selectUserId);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const isGameStarted = useSelector(selectIsGameStarted);
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const hexes = useSelector(selectHexes);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const players = useSelector(selectPlayers);
  const groundBackground = useSelector(selectGroundBackground);
  const startingQuarters = useSelector(selectStartingQuarters);
  const gameResults = useSelector(selectGameResults);
  const playersConfirmedSetupPhase = useSelector(selectPlayersConfirmedSetupPhase);
  const playersWaitedToConfirmCombatStage = useSelector(selectPlayersWaitedToConfirmCombatStage);
  const playersConfirmedCombatStage = useSelector(selectPlayersConfirmedCombatStage);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  const round = useSelector(selectRound);
  const pendingBuildings = useSelector(selectPendingBuildings);
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  const isInitiativeInterceptedWithIsolationism = useSelector(selectIsInitiativeInterceptedWithIsolationism);
  // Одновременная фаза снабжения сетевой игры (см. utils/gameState/supply/parallelSupply)
  const isParallelSupply = useSelector(selectIsParallelSupply);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  // Игрок, за которого этот клиент раздает снабжение: в одновременной фазе —
  // сам пользователь, пока не закончил свою часть (у закончившего и зрителя null)
  const supplyActingPlayer = useSelector(selectSupplyActingPlayer);
  // Чей ход показывает кнопка — тот же игрок в стате шапки (CurrentPhaseInfo) и
  // в цвете рамки (getTurnColorPlayer); вне одновременной фазы — текущий игрок
  const turnDisplayedPlayer = useSelector(selectTurnDisplayedPlayer);

  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [timeLeftFraction, setTimeLeftFraction] = useState(1);
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(0);

  const turnEndTimestamp = useSelector(selectTurnEndTimestamp);
  const isTutorialActive = useSelector(selectIsTutorialActive);
  const timerEnabled = useSelector(selectTimerEnabled);
  const serverTimeDelta = useSelector(selectServerTimeDelta);

  const isParallelTurn = checkIfParallelTurn(phase, warPhaseStage, isParallelSupply);
  const actingPlayer = getActingPlayer(gameType, userId, players, currentPlayer);
  // Время хода игрока вышло, а сервер ход еще не закрыл: до закрытия действия
  // не принимаются (см. turnInputLock) — кнопка показывает «время вышло»
  const isTurnInputLocked = useSelector(selectIsTurnInputLocked);
  // Дела этапа для счетчика на кнопке — того, за кого ходит этот клиент: в
  // одновременной фазе снабжения это сам пользователь, пока своя часть не закончена
  const progressPlayerId = isParallelSupplyPhase ? supplyActingPlayer?.id : actingPlayer?.id;
  const phaseProgress = useSelector((state) => (
    progressPlayerId ? selectPhaseProgress(state, progressPlayerId) : null
  ));

  // Время хода — для этого игрока (см. selectTurnDurationMs): в обычной партии
  // это стандартное время фазы, а в промо-матче против ИИ у агента свой
  // предохранитель до 10 минут. Такой долгий остаток показываем минутами
  const turnDurationMs = useSelector((state) => selectTurnDurationMs(state, actingPlayer?.id));
  const standardTurnDurationMs = useSelector(selectStandardTurnDurationMs);
  const isExtendedTurnTimer = turnDurationMs > standardTurnDurationMs;

  // Этап ближнего боя бота с отрядами живых игроков завершает назначенный живой игрок
  const botMeleeCompletingPlayer = gameType === GAME_TYPES.LOCAL
  && phase === PHASES.WAR
  && warPhaseStage === WAR_PHASE_STAGES.COMBAT
  && currentPlayer?.isBot
    ? getHumanMeleeCompletingPlayer(players, pendingMeleeDamage)
    : null;
  // Остался ли у того, кто подтверждает ближний бой, переброс за
  // дополнительное снабжение: пока да — кнопка без свечения, как «есть дела»
  const meleeConfirmingPlayerId = botMeleeCompletingPlayer?.id || actingPlayer?.id;
  const isCombatRerollPending = useSelector((state) => (
    phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT
    && getMeleeRerollUnitIds(selectUnits(state), pendingMeleeDamage, meleeConfirmingPlayerId).length > 0
  ));
  const {openCard} = usePlayersListSideMenu();
  const {togglePanelsVisible} = useReplayControls();

  const isReplayMode = gameType === GAME_TYPES.REPLAY;
  // Пассивные режимы просмотра: демо-партия меню, режим зрителя локальной
  // партии, воспроизведение записи матча — кнопка не орган управления, а
  // витрина текущего игрока (его цвет + иконки фракции/политики, без
  // статусной иконки check/hourglass)
  const isPassiveWatchMode = isDemoMode || isSpectatorMode || isReplayMode;

  const timerPlayedRef = useRef(false);
  const replayClickTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(replayClickTimerRef.current), []);

  // Эффекту таймера нужны только id игроков: зависимость от объектов
  // перезапускала бы эффект (и обрывала звук таймера) при каждой новой ссылке
  // на того же игрока — а пересчет дохода дает новый объект игрока на каждое
  // изменение поля
  const actingPlayerId = actingPlayer?.id;
  const currentPlayerId = currentPlayer?.id;
  const supplyActingPlayerId = supplyActingPlayer?.id;

  useEffect(() => {
    // В записи матча таймер не показываем: turnEndTimestamp в записи — из
    // таймлайна оригинальной партии, к темпу воспроизведения он отношения не имеет
    if (!turnEndTimestamp || !isGameStarted || gameResults || !timerEnabled || isDemoMode || isReplayMode) {
      setTimeLeftFraction(0);
      return;
    }

    const phaseDurationMs = turnDurationMs;
    if (!phaseDurationMs) {
      setTimeLeftFraction(1);
      return;
    }

    const shouldPlayTimerSound = () => {
      // Одновременная фаза снабжения: таймер звучит, только пока своя часть
      // фазы не закончена — закончившему и зрителю торопиться некуда
      if (isParallelSupplyPhase) {
        return supplyActingPlayerId !== undefined;
      }
      return actingPlayerId !== undefined && (currentPlayerId === actingPlayerId || isParallelTurn);
    };

    const updateFraction = () => {
      const now = Date.now() + serverTimeDelta;
      const rawRemaining = (turnEndTimestamp + phaseDurationMs) - now;
      // Клэмп сверху длительностью фазы: рассинхрон серверного времени
      // (например, устаревшая калибровка serverTimeDelta после восстановления
      // фоновой вкладки) не должен показывать игроку абсурдный остаток хода
      const remaining = Math.min(phaseDurationMs, rawRemaining);
      const fraction = Math.max(0, Math.min(1, remaining / phaseDurationMs));
      setTimeLeftFraction(fraction);
      setTimeLeftSeconds(Math.max(0, Math.ceil(remaining / 1000)));

      if (remaining <= TIME_REMAINING_FOR_TIMER_SOUND_MS && remaining > 0 && !timerPlayedRef.current && shouldPlayTimerSound()) {
        timerPlayedRef.current = true;
        REGISTRY_SINGLETON.getSoundManager().playTimer();
      }
      // Следующий пересчет заказывается по остатку без клэмпа: обрезанный
      // остаток стоит ровно на границе секунды, и, пока клэмп действует,
      // пересчет шел бы каждые два десятка миллисекунд
      return rawRemaining;
    };

    // Пересчет — по границам секунд остатка, а не раз в секунду от запуска
    // эффекта: иначе «1 сек» могла висеть почти секунду после нуля (см.
    // getMsToNextTurnTimerTick). Каждый пересчет заказывает следующий от
    // текущего остатка, так что придушенный в фоне таймер не сбивает шаг.
    // Сбой пересчета (звук таймера еще не загружен: поле пересобирается) отсчет
    // не останавливает — следующий пересчет заказывается в любом случае
    let tickTimeoutId = null;
    const tick = () => {
      let rawRemaining;
      try {
        rawRemaining = updateFraction();
      } catch (error) {
        console.error('[CompletePhaseButton] Сбой пересчета таймера хода:', error);
      }
      tickTimeoutId = setTimeout(tick, getMsToNextTurnTimerTick(rawRemaining));
    };

    timerPlayedRef.current = false;
    tick();
    return () => {
      clearTimeout(tickTimeoutId);
      REGISTRY_SINGLETON.getSoundManager().stopTimer();
      timerPlayedRef.current = false;
    };
  }, [turnEndTimestamp, isGameStarted, phase, warPhaseStage, gameResults, gameType, actingPlayerId, currentPlayerId, isParallelTurn, isParallelSupplyPhase, supplyActingPlayerId, timerEnabled, turnDurationMs, serverTimeDelta, isDemoMode, isReplayMode]);

  const playTurnEndSound = () => {
    REGISTRY_SINGLETON.getSoundManager().playTurnEnd();
  }

  // Одновременная фаза снабжения сетевой игры: игрок завершает СВОЮ часть фазы
  // (как подтверждение расстановки). Фазу закрывает сервер, когда закончили
  // все, — completePhase/startNextPhase отсюда не уходят никогда
  const handleCompleteParallelSupply = (isTurnConfirmationDisabled) => {
    // Состояние — свежее из стора, а не из рендера: к клику игрок уже мог
    // закончить (авто-завершение по таймеру), а фаза — закрыться
    const state = store.getState();
    if (!selectIsParallelSupplyPhase(state)) {
      return;
    }

    const supplyActor = selectSupplyActingPlayer(state);
    if (!supplyActor) {
      const waitedPlayer = selectTurnDisplayedPlayer(state);
      const stateActingPlayer = getActingPlayerFromState(state);
      if (checkIfPlayerCompletedPhase(selectSharedGameState(state), stateActingPlayer?.id) || !waitedPlayer) {
        // Своя часть фазы уже завершена — ждем остальных
        dispatch(showToast(TEXT_SUPPLY_WAIT_OTHERS, 'info', 3000));
      } else {
        // Зритель и запись матча — как в прочих одновременных этапах
        dispatch(showToastWaitOtherPlayerTurn(waitedPlayer));
      }
      return;
    }

    // Фермы, которые еще могут передать снабжение, проверяются у самого
    // действующего игрока: текущий игрок в этой фазе — только якорь хода
    if (!isTurnConfirmationDisabled && checkIfPlayerHasSupplyCapableFarm(
      state, REGISTRY_SINGLETON.getHexGridRenderer(), supplyActor
    )) {
      dispatch(showSupplyFarmsConfirmModal());
      return;
    }

    handleCompleteTurn(store, true, true, REGISTRY_SINGLETON.getSoundManager(), {playerId: supplyActor.id});
  };

  const handleCompletePhase = () => {
    if (gameResults) {
      dispatch(showGameEndModal());
      return;
    }

    // Время хода вышло, сервер вот-вот закроет ход сам (см. turnInputLock):
    // ни завершения, ни подтверждений, ни звука. Состояние — свежее из стора:
    // блокировка могла включиться уже после рендера
    const stateOnClick = store.getState();
    if (checkIfTurnInputBlocked(stateOnClick)) {
      notifyTurnInputBlocked(dispatch, stateOnClick);
      return;
    }

    // Дев-режим (.env.development): подтверждающие модалки завершения хода в
    // фазах снабжения и строительства пропускаются — ход завершается сразу
    const isPhaseConfirmationDisabled = isEnvConfEnabled(
      ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_PHASE_CONFIRMATION_MODAL
    );
    // В обучении подтверждения снабжения и строительства не показываем: там
    // каждый шаг ведет подсказка, и вопрос «точно завершаешь?» поверх нее
    // только сбивает с выверенного сценария
    const isTurnConfirmationDisabled = isTutorialActive || isPhaseConfirmationDisabled;

    if (isParallelSupplyPhase) {
      handleCompleteParallelSupply(isTurnConfirmationDisabled);
      return;
    }

    let shouldCheckCurrentUserTurn = true;
    if (gameType === GAME_TYPES.LOCAL) {
      if (phase === PHASES.FIELD_PREPARATION) {
        shouldCheckCurrentUserTurn = false;
      } else if (botMeleeCompletingPlayer) {
        // Ход бота, но этап ближнего боя завершает живой игрок-участник
        shouldCheckCurrentUserTurn = false;
      }
    } else if (isParallelTurn && actingPlayer && actingPlayer.id !== SPECTATOR_ID) {
      shouldCheckCurrentUserTurn = false;
    }

    if (shouldCheckCurrentUserTurn && !isCurrentUserTurn) {
      dispatch(showToastWaitOtherPlayerTurn(currentPlayer));
      return;
    }

    // Гейт обучения: если текущий шаг не разрешает завершение хода, блокируем
    // ВЕСЬ каскад на входе. Проверять только финальные COMPLETE_PHASE/
    // SET_WAR_PHASE_STAGE в middleware недостаточно: подготовительные APPLY_*
    // системные и успели бы примениться. На ходу бота гейт пропускает —
    // завершение этапа боя бота живым игроком продолжает работать
    const completeAttemptAction = completePhase(Date.now());
    if (!checkTutorialActionWouldApply(store.getState(), completeAttemptAction)) {
      dispatch(completeAttemptAction); // гейт заблокирует и покажет подсказку
      return;
    }

    if (gameType === GAME_TYPES.LOCAL && !isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_PHASE_BUTTON_COOLDOWN)) {
      setIsOnCooldown(true);
      setTimeout(() => setIsOnCooldown(false), HOT_SEAT_COMPLETE_PHASE_COOLDOWN);
    }

    if (gameType !== GAME_TYPES.LOCAL) {
      if (phase === PHASES.SETUP) {
        const playerHasSelectedQuarter = startingQuarters[actingPlayer.id];

        if (!playerHasSelectedQuarter) {
          dispatch(showToast(TEXT_SETUP_STARTING_QUARTER, 'info', 5000));
        } else {
          const isActingPlayerConfirmed = checkIfPlayerConfirmedSetupPhase(playersConfirmedSetupPhase, actingPlayer);

          if (!isActingPlayerConfirmed) {
            dispatch(confirmSetupPhase(actingPlayer.id));
            playTurnEndSound();

            if (checkIfAllPlayersConfirmedStartingQuarter(store.getState())) {
              const setupGuard = createPhaseActionGuard(selectSharedGameState(store.getState()));
              dispatch(completePhase(undefined, setupGuard));
              dispatch(startNextPhase(undefined, getRandomPolicyForMarket(), setupGuard));
            }
          } else {
            dispatch(showToast(TEXT_SETUP_STARTING_QUARTER_WAIT_OTHERS, 'info', 3000));
          }
        }
        return;
      } else if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
        const isActingPlayerWaitedToConfirm = playersWaitedToConfirmCombatStage.includes(actingPlayer.id);
        const isActingPlayerConfirmed = playersConfirmedCombatStage.includes(actingPlayer.id);

        if (!isActingPlayerWaitedToConfirm || isActingPlayerConfirmed) {
          dispatch(showToast(TEXT_COMBAT_STAGE_WAIT_OTHERS, 'info', 3000));
          return;
        }

        dispatch(confirmCombatStage(actingPlayer.id));
        playTurnEndSound();

        if (checkIfAllPlayersConfirmedCombatStage(store.getState())) {
          executeWarPhaseStageCompletion(store, true, false);
        }

        return;
      }
    }

    if (!isGameStarted) {
      // Подсчёт флагов на поле
      const flagsOnField = Object.values(hexes).filter(token =>
        token.startsWith('flag_')
      ).length;

      if (flagsOnField === 0) {
        alert('Невозможно начать матч: на поле нет флагов игроков. Размести хотя бы один флаг перед началом игры.');
        return;
      }

      dispatch(startGame(
        Date.now() + serverTimeDelta, timerEnabled, false,
        // Подложку партия выбрала при загрузке поля (см. GameContent): поле уже
        // нарисовано ею, и разыгрывать новую нельзя — в запись уехала бы не та,
        // что видел игрок. Запасной вариант — старое сохранение без подложки
        getRandomInitiativeShift(players.length), false, groundBackground || getRandomGroundBackground()
      ));
      playTurnEndSound();
      return;
    }

    if (phase === PHASES.SETUP) {
      const playerHasPlacedStartingQuarter = startingQuarters[currentPlayer.id];

      if (!playerHasPlacedStartingQuarter) {
        dispatch(showToast(TEXT_SETUP_STARTING_QUARTER, 'info', 5000));
        return;
      }
    }

    if (phase === PHASES.CONSTRUCTION
      && round <= NO_CONSTRUCTION_ACTIONS_WARNING_MAX_ROUND
      && !isTurnConfirmationDisabled
    ) {
      const usedConstructionActions = calcUsedConstructionActions(
        currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism
      );
      if (usedConstructionActions === 0) {
        dispatch(showNoConstructionActionsModal());
        return;
      }
    }

    if (phase === PHASES.SUPPLY && !isTurnConfirmationDisabled) {
      const hasSupplyCapableFarm = checkIfPlayerHasSupplyCapableFarm(
        store.getState(), REGISTRY_SINGLETON.getHexGridRenderer()
      );
      if (hasSupplyCapableFarm) {
        dispatch(showSupplyFarmsConfirmModal());
        return;
      }
    }

    // Этап войны, а у игрока остались отряды, способные действовать и не
    // пропустившие ход (те же, что ведет автовыбор, см. unit-auto-selection):
    // спрашиваем, точно ли завершать. Окно заменило отдельные подтверждения
    // Лепреконов (кража), Суккубов (соблазнение) и Гоблинов (способность) —
    // их отряды тоже входят в число способных действовать
    if (phase === PHASES.WAR && UNIT_AUTO_SELECTION_STAGES.includes(warPhaseStage)
      && !isTurnConfirmationDisabled
      && progressPlayerId
      // Свежее состояние, а не из рендера: к клику отряд мог только что сходить
      && selectPhaseProgress(store.getState(), progressPlayerId).actionableIds.length > 0
    ) {
      dispatch(showWarStageUnitsConfirmModal());
      return;
    }

    handleCompleteTurn(store, true, true, REGISTRY_SINGLETON.getSoundManager());
  };

  // В реплее и в режиме зрителя локальной партии одиночный и двойной клик
  // различаются вручную, а не через нативный onClick+onDoubleClick: после
  // конца партии handleCompletePhase() открывает GameEndModal, который
  // перекрывает кнопку — если бы это происходило сразу на первом клике,
  // второй клик двойного клика попадал бы уже на оверлей модалки, а не на
  // кнопку, и dblclick до кнопки не доходил. Поэтому одиночный клик
  // откладывается на короткое окно; если за это время придет второй клик —
  // сработает только тогл панелей, а handleCompletePhase() из первого клика
  // не выполнится вовсе. isSpectatorMode здесь покрывает оба случая: реплей
  // всегда приходит с isSpectatorMode=true (App.js выставляет его перед
  // переходом на экран воспроизведения)
  const handleButtonClick = (e) => {
    e.stopPropagation();

    if (!isSpectatorMode) {
      handleCompletePhase();
      return;
    }

    if (replayClickTimerRef.current) {
      clearTimeout(replayClickTimerRef.current);
      replayClickTimerRef.current = null;
      togglePanelsVisible();
      return;
    }

    replayClickTimerRef.current = setTimeout(() => {
      replayClickTimerRef.current = null;
      handleCompletePhase();
    }, REPLAY_CLICK_DISAMBIGUATION_MS);
  };

  let iconImage
  let displayedPlayer
  let isActingPlayerConfirmedSetup
  if (gameResults) {
    iconImage = CHECK_ICON_PATH;
  } else if (isParallelTurn) {
    if (phase === PHASES.SETUP) {
      if (gameType === GAME_TYPES.LOCAL && currentPlayer.isBot) {
        iconImage = HOURGLASS_ICON_PATH;
        displayedPlayer = currentPlayer;
      } else {
        isActingPlayerConfirmedSetup = checkIfPlayerConfirmedSetupPhase(playersConfirmedSetupPhase, actingPlayer);

        if (isActingPlayerConfirmedSetup) {
          iconImage = HOURGLASS_ICON_PATH;
          const notConfirmedPlayer = players.find(player => !checkIfPlayerConfirmedSetupPhase(playersConfirmedSetupPhase, player));
          displayedPlayer = notConfirmedPlayer;
        } else {
          iconImage = CHECK_ICON_PATH;
          displayedPlayer = actingPlayer;
        }
      }
    } else if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
      if (gameType === GAME_TYPES.LOCAL && currentPlayer.isBot) {
        if (botMeleeCompletingPlayer) {
          // Назначенный живой игрок завершает этап ближнего боя бота
          iconImage = CHECK_ICON_PATH;
          displayedPlayer = botMeleeCompletingPlayer;
        } else {
          iconImage = HOURGLASS_ICON_PATH;
          displayedPlayer = currentPlayer;
        }
      } else {
        const isActingPlayerWaitedToConfirm = playersWaitedToConfirmCombatStage.includes(actingPlayer.id);
        const isActingPlayerConfirmed = playersConfirmedCombatStage.includes(actingPlayer.id);

        if (isActingPlayerWaitedToConfirm && !isActingPlayerConfirmed) {
          iconImage = CHECK_ICON_PATH;
          displayedPlayer = actingPlayer;
        } else {
          iconImage = HOURGLASS_ICON_PATH;
          const notConfirmedPlayer = players.find(player => {
            return playersWaitedToConfirmCombatStage.includes(player.id) && !playersConfirmedCombatStage.includes(player.id);
          });
          displayedPlayer = notConfirmedPlayer;
        }
      }
    } else if (phase === PHASES.SUPPLY) {
      // Одновременная фаза снабжения: пока своя часть не закончена — кнопка
      // самого игрока с галочкой, потом (и у зрителя) — первого еще не
      // закончившего игрока с часами
      iconImage = supplyActingPlayer ? CHECK_ICON_PATH : HOURGLASS_ICON_PATH;
      displayedPlayer = turnDisplayedPlayer;
    }
  } else {
    if (isCurrentUserTurn || phase === PHASES.FIELD_PREPARATION) {
      iconImage = CHECK_ICON_PATH;
    } else {
      iconImage = HOURGLASS_ICON_PATH;
    }
    displayedPlayer = currentPlayer;
  }

  // Пассивные режимы просмотра: всегда показываем игрока хода — его цвет и
  // иконки фракции/политики (как это уже делает демо-партия меню). Игрок хода —
  // текущий игрок, а в одновременной фазе снабжения первый еще не закончивший
  // (selectTurnDisplayedPlayer — тот же, что в стате шапки и цвете рамки). Без этого
  // запись матча попадала бы в онлайн-зрительские ветки выше (спектаторский
  // серый + заглушка вместо иконки фракции), а не в витрину хода игрока.
  // !gameResults в обоих условиях обязателен: после конца игры показывать
  // нечего (нет "текущего хода"), кнопка должна выглядеть как обычная
  // завершенная — просто иконка check (см. рендер ниже), без фракции/политики
  if (isPassiveWatchMode && !gameResults && isGameStarted && turnDisplayedPlayer) {
    displayedPlayer = turnDisplayedPlayer;
  }
  if (isPassiveWatchMode && !displayedPlayer && !gameResults) {
    displayedPlayer = turnDisplayedPlayer;
  }

  const isBlinkingTimer = timeLeftSeconds <= TIME_REMAINING_FOR_TIMER_SOUND_SEC;

  // Что показывает кнопка и каким цветом (getCompleteButtonView,
  // getCompleteButtonColor). «Свой ход» — ветки выше выбрали галочку, а не
  // часы; на ближнем бою галочка — это неподтвержденный этап
  const isCombatStage = phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT;
  const isOwnTurn = isGameStarted && iconImage === CHECK_ICON_PATH;
  const view = getCompleteButtonView({
    phase,
    warPhaseStage,
    gameResults,
    progress: phaseProgress,
    isOwnTurn,
    isCombatConfirmPending: isCombatStage && isOwnTurn,
    isCombatRerollPending,
    isTurnInputLocked,
    isPassiveWatchMode,
    // Расстановка: квартал того, за кого ходит клиент (в hot-seat — текущий
    // игрок), как и в проверке перед подтверждением
    isStartingQuarterPlaced: Boolean(actingPlayer && startingQuarters?.[actingPlayer.id]),
  });
  const phaseButtonColor = getCompleteButtonColor({
    phase,
    warPhaseStage,
    gameResults,
    isOwnTurn,
    turnPlayer: displayedPlayer,
    warPhasePlayer: currentPlayer,
    ownPlayerId: isPassiveWatchMode || actingPlayer?.id === SPECTATOR_ID ? null : actingPlayer?.id,
  });
  const pulse = useCompleteButtonPulse(view);

  // Enter, пока дела остались: предупреждение фазы вместо завершения — то же
  // окно, что показывает клик, но и там, где клик его не показал бы (дев-режим
  // без подтверждений, строительство после первого действия)
  const handleEnterWithDeedsLeft = () => {
    const state = store.getState();
    const progress = progressPlayerId ? selectPhaseProgress(state, progressPlayerId) : null;
    if (!progress?.actionableIds.length) {
      // Найм и прочие этапы без счетчика: предупреждать не о чем
      return;
    }
    if (phase === PHASES.SUPPLY) {
      dispatch(showSupplyFarmsConfirmModal());
    } else if (phase === PHASES.CONSTRUCTION) {
      dispatch(showNoConstructionActionsModal());
    } else if (phase === PHASES.WAR && UNIT_AUTO_SELECTION_STAGES.includes(warPhaseStage)) {
      dispatch(showWarStageUnitsConfirmModal());
    }
  };
  const isEnterKeyEnabled = !isPassiveWatchMode && !isTutorialActive && !isOnCooldown
    && actingPlayer?.id !== SPECTATOR_ID;
  useCompletePhaseEnterKey({
    mode: view.mode,
    isEnabled: isEnterKeyEnabled,
    stageKey: `${round}|${phase}|${warPhaseStage}|${currentPlayer?.id}|${progressPlayerId}`,
    onComplete: handleCompletePhase,
    onWarn: handleEnterWithDeedsLeft,
  });

  // Эмаль цвета фазы или соперника; на перезарядке hot-seat — матовая
  const buttonColor = isOnCooldown ? WIN_POINTS_COLOR_DARK : phaseButtonColor;

  const isTimerShown = !gameResults && isGameStarted && Boolean(turnEndTimestamp) && timerEnabled
    && !isDemoMode && !isReplayMode;

  // Время хода — текст над кнопкой. Вне самой кнопки: у плиты срезаны углы
  // через clip-path, и все, что торчит за ее края, обрезалось бы
  const timerText = isTurnInputLocked ? (
    // В две строки: одной строкой надпись шире плиты, и кнопка, выровненная
    // по центру надписи, съезжала бы влево
    <div className={`complete-phase-button-timer-text-container complete-phase-button-timer-text-container_stacked`}>
      <span className={`complete-phase-button-timer-text`}>время</span>
      <span className={`complete-phase-button-timer-text`}>вышло</span>
    </div>
  ) : (isTimerShown && timeLeftSeconds > 0) ? (
    <div className={`complete-phase-button-timer-text-container`}>
      {isExtendedTurnTimer ? (
        <span className={`complete-phase-button-timer-text ${addClassNameIfTrue(isBlinkingTimer, 'blinking')}`}>
          {formatTurnTimeLeftMinutes(timeLeftSeconds)}
        </span>
      ) : (
        <>
          <span className={`complete-phase-button-timer-text ${addClassNameIfTrue(isBlinkingTimer, 'blinking')}`}>
            {timeLeftSeconds}
          </span>
          <span className={`complete-phase-button-timer-text ${addClassNameIfTrue(isBlinkingTimer, 'blinking')}`}>
            сек
          </span>
        </>
      )}
    </div>
  ) : null;

  // В обучении снапшотные главы стартуют через loadState без startGame —
  // turnEndTimestamp пуст, но портрет на кнопке нужен (шаг «открой карточку
  // фракции с кнопки»)
  const isContentShown = Boolean(gameResults) || (isGameStarted && Boolean(turnEndTimestamp || isTutorialActive));

  const handlePortraitClick = (isDemoMode || isSpectatorMode) ? undefined : (e) => {
    if (view.mode === COMPLETE_BUTTON_MODES.WAITING) {
      e.stopPropagation();
      REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
      openCard(displayedPlayer, null, CARD_OPEN_SOURCES.COMPLETE_BUTTON);
    }
  };

  return (
    <>
      {!isDemoMode && <PhaseAutoComplitioner/>}
      {!isDemoMode && <LocalTurnTimer/>}

      <CompletePhaseButtonPlate
        view={view}
        buttonColor={buttonColor}
        displayedPlayer={displayedPlayer}
        timerText={timerText}
        isTimerShown={isTimerShown}
        timeLeftFraction={timeLeftFraction}
        isHurry={isTimerShown && isBlinkingTimer}
        isContentShown={isContentShown}
        pulse={pulse}
        isFieldInset={!isDemoMode}
        disabled={isOnCooldown}
        cursor={(isDemoMode || isTurnInputLocked) ? 'default' : (isOnCooldown ? 'not-allowed' : 'pointer')}
        // Кнопка в ожидании хода другого игрока — стрелка обучения на ней
        // показывает «ждать», а не «нажать» (TutorialUiArrow)
        isTutorialWaiting={iconImage === HOURGLASS_ICON_PATH}
        onClick={isDemoMode ? undefined : handleButtonClick}
        title={isEnterKeyEnabled && checkIfEnterKeyHandledMode(view.mode) ? 'Завершить этап (Enter)' : undefined}
        onPortraitClick={handlePortraitClick}
      />
    </>
  );
};

export default CompletePhaseButton;
